import { resolveApiBase } from './apiBase';

function apiBase() {
  return resolveApiBase();
}

export const TOKEN_KEY = 'auth_token';
const ROLE_KEY = 'auth_role';
const USER_KEY = 'auth_user';

// Back-compat: migrate old MVP keys once if present.
(function migrateLegacySession() {
  try {
    const legacy = localStorage.getItem('admin_token');
    if (legacy && !localStorage.getItem(TOKEN_KEY)) {
      localStorage.setItem(TOKEN_KEY, legacy);
      localStorage.setItem(ROLE_KEY, localStorage.getItem('admin_role') || 'admin');
    }
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_role');
  } catch (_) {
    /* ignore */
  }
})();

export function getAdminToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function getAdminRole() {
  return localStorage.getItem(ROLE_KEY) || 'viewer';
}

export function getAuthUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export function setAdminSession(token, role = 'viewer', user = null) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(ROLE_KEY, role);
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  else localStorage.removeItem(USER_KEY);
}

export function clearAdminSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ROLE_KEY);
  localStorage.removeItem(USER_KEY);
}

function authHeaders() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(path, options = {}) {
  const url = `${apiBase()}/api${path}`;
  let res;
  try {
    res = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
        ...(options.headers || {}),
      },
    });
  } catch (err) {
    throw new Error(
      `Cannot reach API at ${url}. Ensure the backend is listening on port ${process.env.REACT_APP_API_PORT || '3000'} and set REACT_APP_API_BASE (or PUBLIC_API_URL in Docker) to your server URL, e.g. http://<ec2-ip>:3000.`
    );
  }
  if (res.status === 401) {
    clearAdminSession();
  }
  if (res.status === 204) return null;
  const text = await res.text();
  if (!res.ok) {
    let message = `${options.method || 'GET'} ${path} failed: ${res.status}`;
    try {
      const body = text ? JSON.parse(text) : {};
      if (body.message) message = body.message;
      else if (body.error) message = body.error;
    } catch (_) {
      /* ignore */
    }
    throw new Error(message);
  }
  if (!text) return null;
  return JSON.parse(text);
}

export async function apiGet(path) {
  return request(path, { method: 'GET' });
}

export async function apiPost(path, body) {
  return request(path, { method: 'POST', body: JSON.stringify(body || {}) });
}

export async function apiPut(path, body) {
  return request(path, { method: 'PUT', body: JSON.stringify(body || {}) });
}

export async function apiDelete(path) {
  return request(path, { method: 'DELETE' });
}

/** Public login — does not send an existing bearer token. */
export async function apiLogin(email, password) {
  const url = `${apiBase()}/api/auth/login`;
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch (_) {
    throw new Error(`Cannot reach API at ${url}.`);
  }
  const text = await res.text();
  let body = {};
  try {
    body = text ? JSON.parse(text) : {};
  } catch (_) {
    /* ignore */
  }
  if (!res.ok) {
    throw new Error(body.message || body.error || 'Sign in failed');
  }
  return body;
}
